import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './BlogList.css';
import { blogAPI } from '../../../api';
import { FaArrowLeft } from 'react-icons/fa';

const BlogList = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        fetchBlogs();
    }, []);

    // Récupérer l'URL de base
    const getBaseUrl = () => {
        const apiUrl = process.env.REACT_APP_API_URL;
        
        if (apiUrl) {
            return apiUrl.replace('/api', '');
        } else if (window.location.origin.includes('localhost') || window.location.origin.includes('127.0.0.1')) {
            return 'http://127.0.0.1:8000';
        } else {
            return window.location.origin;
        }
    };

    // Fonction pour le fallback d'image SVG local
    const getFallbackImage = (blogTitle = 'Blog ETEF') => {
        return `data:image/svg+xml;utf8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="350" height="200" viewBox="0 0 350 200">
                <rect width="350" height="200" fill="#f8f9fa"/>
                <text x="175" y="100" font-family="Arial, sans-serif" font-size="16" text-anchor="middle" fill="#6c757d" dy=".3em">
                    Blog ETEF
                </text>
                <text x="175" y="120" font-family="Arial, sans-serif" font-size="12" text-anchor="middle" fill="#adb5bd" dy=".3em">
                    ${blogTitle}
                </text>
            </svg>
        `)}`;
    };

    const fetchBlogs = async (page = 1) => {
        try {
            setLoading(true);
            const response = await blogAPI.getMyBlogs(page);
            
            console.log('Blogs API Response (admin):', response);
            
            if (response.success && response.data) {
                setBlogs(response.data.data || []);
                setPagination({
                    currentPage: response.data.current_page || 1,
                    totalPages: response.data.last_page || 1,
                    totalItems: response.data.total || 0
                });
            }
        } catch (error) {
            console.error('Erreur lors du chargement des blogs:', error);
            alert(error.message || 'Erreur lors du chargement des blogs');
        } finally {
            setLoading(false);
        }
    };

    // Gestionnaire d'erreur d'image avec fallback
    const handleImageError = (e, blog) => {
        console.error('Failed to load blog image:', {
            blogId: blog?.id,
            blogTitle: blog?.title,
            attemptedUrl: e.target.src,
            imageUrl: blog?.image_url || blog?.image
        });
        
        e.target.src = getFallbackImage(blog?.title);
        e.target.style.opacity = '0.8';
        e.target.style.filter = 'grayscale(20%)';
    };

    const handleDelete = async (id, title) => {
        if (window.confirm(`Êtes-vous sûr de vouloir supprimer le blog "${title}" ?`)) {
            try {
                await blogAPI.delete(id);
                setBlogs(blogs.filter(blog => blog.id !== id));
                alert('Blog supprimé avec succès');
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                alert(error.message || 'Erreur lors de la suppression');
            }
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await blogAPI.update(id, { is_published: newStatus });
            
            setBlogs(blogs.map(blog => 
                blog.id === id ? { ...blog, is_published: newStatus } : blog
            ));
            
            alert(`Blog ${newStatus ? 'publié' : 'dépublié'} avec succès`);
        } catch (error) {
            console.error('Erreur lors du changement de statut:', error);
            alert(error.message || 'Erreur lors du changement de statut');
        }
    };

    const filteredBlogs = blogs.filter(blog => {
        const matchesSearch = searchTerm === '' || 
            blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (blog.category && blog.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (blog.content && blog.content.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesStatus = filterStatus === 'all' || 
            (filterStatus === 'published' && blog.is_published) ||
            (filterStatus === 'draft' && !blog.is_published);
        
        return matchesSearch && matchesStatus;
    });

    const formatDate = (dateString) => {
        if (!dateString) return 'Date non disponible';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="blog-list-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Chargement des blogs...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="blog-list-container">
            <Link 
                to="/admin" 
                className="btn-back-admin"
                title="Retour à l'administration"
            >   
                <FaArrowLeft className="back-arrow-icon" />
            </Link>
            <div className="blog-list-header">
                <h1>Gestion des Blogs</h1>
                <Link to="/admin/blogs/nouveau" className="btn btn-primary">
                    <span className="btn-icon">+</span>
                    Nouveau Blog
                </Link>
            </div>

            <div className="filters-section">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Rechercher un blog..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                    <span className="search-icon">🔍</span>
                </div>

                <div className="filter-buttons">
                    <button 
                        className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('all')}
                    >
                        Tous ({blogs.length})
                    </button>
                    <button 
                        className={`filter-btn ${filterStatus === 'published' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('published')}
                    >
                        Publiés ({blogs.filter(b => b.is_published).length})
                    </button>
                    <button 
                        className={`filter-btn ${filterStatus === 'draft' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('draft')}
                    >
                        Brouillons ({blogs.filter(b => !b.is_published).length})
                    </button>
                </div>
            </div>

            <div className="stats-bar">
                <div className="stat-item">
                    <span className="stat-label">Total :</span>
                    <span className="stat-value">{pagination.totalItems}</span>
                </div>
                <div className="stat-item">
                    <span className="stat-label">Affichés :</span>
                    <span className="stat-value">{filteredBlogs.length}</span>
                </div>
            </div>

            <div className="blog-list">
                {filteredBlogs.length > 0 ? (
                    <div className="blog-grid">
                        {filteredBlogs.map(blog => (
                            <div key={blog.id} className="blog-card">
                                <div className="blog-card-header">
                                    <div className="image-container">
                                        <img 
                                            src={blog.image_url || blog.image || getFallbackImage(blog.title)}
                                            alt={blog.title}
                                            className="blog-image"
                                            onError={(e) => handleImageError(e, blog)}
                                        />
                                    </div>
                                    <div className="blog-status">
                                        <span className={`status-badge ${blog.is_published ? 'published' : 'draft'}`}>
                                            {blog.is_published ? 'Publié' : 'Brouillon'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="blog-card-body">
                                    <h3 className="blog-title">{blog.title}</h3>
                                    
                                    <div className="blog-meta">
                                        <span className="meta-item">
                                            <span className="meta-icon">📅</span>
                                            {formatDate(blog.created_at || blog.published_date)}
                                        </span>
                                        {blog.category && (
                                            <span className="meta-item">
                                                <span className="meta-icon">🏷️</span>
                                                {blog.category}
                                            </span>
                                        )}
                                        {blog.author && (
                                            <span className="meta-item">
                                                <span className="meta-icon">✍️</span>
                                                {blog.author}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="blog-excerpt">
                                        {blog.content ? (
                                            blog.content.length > 150 
                                                ? `${blog.content.substring(0, 150)}...`
                                                : blog.content
                                        ) : (
                                            <span className="no-content">Pas de contenu</span>
                                        )}
                                    </div>
                                </div>
                                
                                <div className="blog-card-footer">
                                    <div className="blog-actions">
                                        <Link 
                                            to={`/blog/${blog.slug || blog.id}`} 
                                            className="btn btn-view"
                                            title="Voir"
                                            target="_blank"
                                        >
                                            👁️
                                        </Link>
                                        <Link 
                                            to={`/admin/blogs/${blog.id}/modifier`} 
                                            className="btn btn-edit"
                                            title="Modifier"
                                        >
                                            ✏️
                                        </Link>
                                        <button 
                                            onClick={() => handleToggleStatus(blog.id, blog.is_published)}
                                            className={`btn ${blog.is_published ? 'btn-unpublish' : 'btn-publish'}`}
                                            title={blog.is_published ? 'Dépublier' : 'Publier'}
                                        >
                                            {blog.is_published ? '🚫' : '✅'}
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(blog.id, blog.title)}
                                            className="btn btn-delete"
                                            title="Supprimer"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="no-results">
                        <div className="empty-state">
                            <span className="empty-icon">📄</span>
                            <h3>Aucun blog trouvé</h3>
                            <p>
                                {searchTerm || filterStatus !== 'all' 
                                    ? 'Essayez de modifier vos critères de recherche' 
                                    : 'Commencez par créer votre premier blog'}
                            </p>
                            {!searchTerm && filterStatus === 'all' && (
                                <Link to="/admin/blogs/nouveau" className="btn btn-primary">
                                    Créer mon premier blog
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {pagination.totalPages > 1 && (
                <div className="pagination">
                    <button 
                        className="page-btn prev"
                        onClick={() => fetchBlogs(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        ← Précédent
                    </button>
                    
                    <div className="page-numbers">
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            let pageNum;
                            if (pagination.totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (pagination.currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                pageNum = pagination.totalPages - 4 + i;
                            } else {
                                pageNum = pagination.currentPage - 2 + i;
                            }
                            
                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => fetchBlogs(pageNum)}
                                    className={`page-btn ${pageNum === pagination.currentPage ? 'active' : ''}`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                    </div>
                    
                    <button 
                        className="page-btn next"
                        onClick={() => fetchBlogs(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        Suivant →
                    </button>
                </div>
            )}
        </div>
    );
};

export default BlogList;